/**
 * ModeIndicatorBanner — persistent on-screen indicator for targeting modes
 * (move / attack / mine / relocate) that works on both desktop and touch devices.
 *
 * On desktop, users get the cursor change (crosshair / not-allowed) AND this banner.
 * On touch, the banner is the primary signal that the game is in a targeting mode.
 *
 * Listens to EventBus events:
 *   - mmo-movement-mode-started / mmo-movement-mode-cancelled
 *   - mmo-attack-mode-started   / mmo-attack-mode-cancelled
 *   - mmo-mine-mode-started     / mmo-mine-mode-cancelled
 *
 * On Cancel tap, emits:
 *   - mmo-cancel-current-mode  (MMOGameScene routes to the active cancel*Mode method)
 */

import Phaser from 'phaser';
import { EventBus } from '../../utils/EventBus';
import { Colors, CSSColors, Fonts, Depth } from './GameUITheme';
import { getSafeAreaInsets } from '../../utils/SafeArea';

type ModeKey = 'movement' | 'attack' | 'mine';

interface ModeConfig {
  label: string;
  accent: number;   // 0xRRGGBB
  accentCss: string;
  hint: string;
  icon: string;     // emoji / unicode glyph
}

const MODE_CONFIGS: Record<ModeKey, ModeConfig> = {
  movement: {
    label: 'MOVE MODE',
    accent: 0xff4444,
    accentCss: '#ff4444',
    hint: 'Tap a hex to move. Tap Cancel to exit.',
    icon: '\u27A4', // black rightwards arrowhead
  },
  attack: {
    label: 'ATTACK MODE',
    accent: 0xff0055,
    accentCss: '#ff0055',
    hint: 'Tap a valid target. Tap Cancel to exit.',
    icon: '\u2694', // crossed swords
  },
  mine: {
    label: 'MINE MODE',
    accent: 0xffaa00,
    accentCss: '#ffaa00',
    hint: 'Tap an asteroid to mine. Tap Cancel to exit.',
    icon: '\u26CF', // pick
  },
};

export class ModeIndicatorBanner {
  private scene: Phaser.Scene;
  private container: Phaser.GameObjects.Container;
  private bg: Phaser.GameObjects.Graphics;
  private iconText: Phaser.GameObjects.Text;
  private labelText: Phaser.GameObjects.Text;
  private hintText: Phaser.GameObjects.Text;
  private cancelButton: Phaser.GameObjects.Container;
  private cancelBg: Phaser.GameObjects.Graphics;
  private cancelText: Phaser.GameObjects.Text;
  private width = 340;
  private height = 56;
  private visible = false;
  private pulseTween?: Phaser.Tweens.Tween;

  // Stored handler refs so we can off() cleanly
  private onMoveStart  = (p: { unit: any }) => this.show('movement', p);
  private onMoveEnd    = () => this.hide();
  private onAttackStart = (p: { unit: any }) => this.show('attack', p);
  private onAttackEnd   = () => this.hide();
  private onMineStart   = (p: { unit: any }) => this.show('mine', p);
  private onMineEnd     = () => this.hide();

  constructor(scene: Phaser.Scene) {
    this.scene = scene;

    // Sizing: narrower on small screens to fit the viewport
    const camWidth = scene.cameras.main.width;
    if (camWidth < 400) {
      this.width = Math.max(260, camWidth - 16);
    }

    this.container = scene.add.container(0, 0);
    this.container.setDepth(Depth.topBar + 5);
    this.container.setScrollFactor(0);
    this.container.setVisible(false);

    this.bg = scene.add.graphics();
    this.container.add(this.bg);

    this.iconText = scene.add.text(0, 0, '', {
      fontSize: '22px',
      color: '#ffffff',
      fontFamily: Fonts.body,
    });
    this.iconText.setOrigin(0, 0.5);
    this.container.add(this.iconText);

    this.labelText = scene.add.text(0, 0, '', {
      fontSize: '14px',
      color: '#ffffff',
      fontFamily: Fonts.heading,
      fontStyle: '900',
      letterSpacing: 2,
    } as Phaser.Types.GameObjects.Text.TextStyle);
    this.labelText.setOrigin(0, 0);
    this.container.add(this.labelText);

    this.hintText = scene.add.text(0, 0, '', {
      fontSize: '11px',
      color: CSSColors.textMuted ?? '#88aac0',
      fontFamily: Fonts.body,
    });
    this.hintText.setOrigin(0, 0);
    this.container.add(this.hintText);

    // Cancel button (right side)
    this.cancelButton = scene.add.container(0, 0);
    this.cancelBg = scene.add.graphics();
    this.cancelText = scene.add.text(0, 0, 'CANCEL', {
      fontSize: '12px',
      color: '#ffffff',
      fontFamily: Fonts.heading,
      fontStyle: '900',
      letterSpacing: 1,
    } as Phaser.Types.GameObjects.Text.TextStyle);
    this.cancelText.setOrigin(0.5);
    this.cancelButton.add([this.cancelBg, this.cancelText]);
    this.container.add(this.cancelButton);

    // Re-position on viewport resize
    scene.scale.on('resize', this.onResize, this);

    // Subscribe to mode events
    EventBus.on('mmo-movement-mode-started', this.onMoveStart, this);
    EventBus.on('mmo-movement-mode-cancelled', this.onMoveEnd, this);
    EventBus.on('mmo-attack-mode-started', this.onAttackStart, this);
    EventBus.on('mmo-attack-mode-cancelled', this.onAttackEnd, this);
    EventBus.on('mmo-mine-mode-started', this.onMineStart, this);
    EventBus.on('mmo-mine-mode-cancelled', this.onMineEnd, this);

    // Cleanup on scene shutdown
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.destroy());
  }

  // ── Layout ────────────────────────────────────────────────────────────

  private layout(): void {
    const camWidth = this.scene.cameras.main.width;
    const insets = getSafeAreaInsets();

    // Top-center anchor, below the top bar (56) + safe area
    const TOP_BAR_HEIGHT = 56;
    const bannerY = insets.top + TOP_BAR_HEIGHT + 4;
    const bannerX = Math.round((camWidth - this.width) / 2);

    this.container.setPosition(bannerX, bannerY);

    const padX = 14;
    const cancelW = 74;
    const cancelH = 32;

    // Icon: left side
    this.iconText.setPosition(padX, this.height / 2);

    // Label + hint: next to icon
    this.labelText.setPosition(padX + 30, 8);
    this.hintText.setPosition(padX + 30, 30);

    // Cancel button: right side
    const cancelX = this.width - cancelW - padX;
    const cancelY = (this.height - cancelH) / 2;
    this.cancelButton.setPosition(cancelX, cancelY);

    // Cancel bg + hit zone
    this.cancelBg.clear();
    this.cancelBg.fillStyle(0xffffff, 0.08);
    this.cancelBg.fillRoundedRect(0, 0, cancelW, cancelH, 16);
    this.cancelBg.lineStyle(1, 0xffffff, 0.45);
    this.cancelBg.strokeRoundedRect(0, 0, cancelW, cancelH, 16);
    this.cancelText.setPosition(cancelW / 2, cancelH / 2);

    // Cancel hit zone — create once
    if (!(this.cancelButton as any)._hitSet) {
      this.cancelButton.setSize(cancelW, cancelH);
      this.cancelButton.setInteractive(
        new Phaser.Geom.Rectangle(0, 0, cancelW, cancelH),
        Phaser.Geom.Rectangle.Contains,
      );
      this.cancelButton.on('pointerdown', () => {
        EventBus.emit('mmo-cancel-current-mode');
      });
      this.cancelButton.on('pointerover', () => {
        this.cancelBg.clear();
        this.cancelBg.fillStyle(0xffffff, 0.18);
        this.cancelBg.fillRoundedRect(0, 0, cancelW, cancelH, 16);
        this.cancelBg.lineStyle(1.5, 0xffffff, 0.7);
        this.cancelBg.strokeRoundedRect(0, 0, cancelW, cancelH, 16);
      });
      this.cancelButton.on('pointerout', () => {
        this.cancelBg.clear();
        this.cancelBg.fillStyle(0xffffff, 0.08);
        this.cancelBg.fillRoundedRect(0, 0, cancelW, cancelH, 16);
        this.cancelBg.lineStyle(1, 0xffffff, 0.45);
        this.cancelBg.strokeRoundedRect(0, 0, cancelW, cancelH, 16);
      });
      (this.cancelButton as any)._hitSet = true;
    }
  }

  private drawBg(accent: number, borderAlpha: number): void {
    this.bg.clear();
    // Dark panel
    this.bg.fillStyle(0x0a1120, 0.92);
    this.bg.fillRoundedRect(0, 0, this.width, this.height, 12);
    // Accent border
    this.bg.lineStyle(2, accent, borderAlpha);
    this.bg.strokeRoundedRect(0, 0, this.width, this.height, 12);
    // Accent bar along the left edge
    this.bg.fillStyle(accent, 0.9);
    this.bg.fillRoundedRect(0, 0, 4, this.height, 2);
  }

  private onResize(): void {
    // Update width based on new camera width
    const camWidth = this.scene.cameras.main.width;
    this.width = camWidth < 400 ? Math.max(260, camWidth - 16) : 340;
    if (this.visible) {
      // Redraw bg at new width
      const mode = this.currentMode;
      if (mode) this.drawBg(MODE_CONFIGS[mode].accent, 0.8);
      this.layout();
    }
  }

  // ── Show / Hide ───────────────────────────────────────────────────────

  private currentMode: ModeKey | null = null;

  private show(mode: ModeKey, _payload?: { unit: any }): void {
    this.currentMode = mode;
    const cfg = MODE_CONFIGS[mode];

    this.iconText.setText(cfg.icon);
    this.iconText.setColor(cfg.accentCss);
    this.labelText.setText(cfg.label);
    this.labelText.setColor(cfg.accentCss);
    this.hintText.setText(cfg.hint);

    this.drawBg(cfg.accent, 0.8);
    this.layout();

    this.container.setVisible(true);
    this.container.setAlpha(0);
    this.container.setY(this.container.y - 16);
    this.visible = true;

    // Fade-in
    this.scene.tweens.add({
      targets: this.container,
      alpha: 1,
      y: this.container.y + 16,
      duration: 220,
      ease: 'Power2',
    });

    // Subtle pulsing border
    this.stopPulse();
    this.pulseTween = this.scene.tweens.add({
      targets: { val: 0.8 },
      val: 0.4,
      duration: 900,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
      onUpdate: (_t, target: { val: number }) => {
        this.drawBg(cfg.accent, target.val);
      },
    });
  }

  private hide(): void {
    if (!this.visible) return;
    this.visible = false;
    this.currentMode = null;
    this.stopPulse();

    this.scene.tweens.add({
      targets: this.container,
      alpha: 0,
      duration: 180,
      ease: 'Power2',
      onComplete: () => {
        this.container.setVisible(false);
      },
    });
  }

  private stopPulse(): void {
    if (this.pulseTween) {
      this.pulseTween.stop();
      this.pulseTween = undefined;
    }
  }

  public destroy(): void {
    this.stopPulse();
    this.scene.scale.off('resize', this.onResize, this);
    EventBus.off('mmo-movement-mode-started', this.onMoveStart, this);
    EventBus.off('mmo-movement-mode-cancelled', this.onMoveEnd, this);
    EventBus.off('mmo-attack-mode-started', this.onAttackStart, this);
    EventBus.off('mmo-attack-mode-cancelled', this.onAttackEnd, this);
    EventBus.off('mmo-mine-mode-started', this.onMineStart, this);
    EventBus.off('mmo-mine-mode-cancelled', this.onMineEnd, this);
    this.container.destroy();
  }
}

// Silence unused-var warning for the imported Colors helper (kept for future use).
void Colors;
